"use client";

import BubbleChartOutlinedIcon from "@mui/icons-material/BubbleChartOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import KeyIcon from "@mui/icons-material/Key";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { Avatar, Button, Divider, Icon, Tooltip } from "@mui/material";
import { useStore } from "@nanostores/react";
import CommentIcon from "common/components/Icons/CommentIcon";
import FileChartIcon from "common/components/Icons/FileChartIcon";
import HelpIcon from "common/components/Icons/HelpIcon";
import InventoryIcon from "common/components/Icons/InventoryIcon";
import SparklesIcon from "common/components/Icons/SparklesIcon";
import SupplierOutreachIcon from "common/components/Icons/SupplierOutreachIcon";
import TruckIcon from "common/components/Icons/TruckIcon";
import {
	railExpandedStore,
	toggleRail,
} from "common/stores/NavigationRailStore";
import { user } from "common/stores/UserStore";
import { GenericObject } from "common/types/Survey";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { Suspense } from "react";
import ListIcon from "common/components/Icons/ListIcon";

export interface NavItemProps {
	label: string;
	path: string;
	icon?: React.ReactElement<typeof Icon>;
	getIsCurrentNavLocation?: (pathname: string) => boolean;
	onClick?: () => void;
	prefetch?: boolean;
	target?: string;
	rel?: string;
}

export interface LeftRailProps {
	items?: Array<NavItemProps>;
	bottomItems?: Array<NavItemProps>;
}

const NavItemTooltipWrapper = ({ title, children }) => {
	const $expanded = useStore(railExpandedStore);
	return $expanded ? (
		children
	) : (
		<Tooltip arrow placement="right" title={title}>
			{children}
		</Tooltip>
	);
};

const NavItem = ({
	label,
	path,
	getIsCurrentNavLocation,
	onClick,
	icon,
	prefetch,
	target,
	rel,
}: NavItemProps) => {
	const $expanded = useStore(railExpandedStore);
	const pathname = usePathname();

	const isCurrentNavLocation =
		getIsCurrentNavLocation?.(pathname) || pathname?.includes(path);

	return (
		<li role="none">
			<NavItemTooltipWrapper title={label}>
				<Button
					href={path}
					component={Link}
					color="secondary"
					className={`navLink ${isCurrentNavLocation ? "selected" : ""} ${$expanded ? "" : "iconOnly"}`}
					role="menuitem"
					aria-label={`Route to ${label}`}
					size="large"
					onClick={onClick}
					startIcon={icon}
					prefetch={prefetch}
					target={target}
					rel={rel}
				>
					{$expanded ? label : undefined}
				</Button>
			</NavItemTooltipWrapper>
		</li>
	);
};

const TopNavItems = ({
	access,
	additionalNavItems,
}: {
	access: GenericObject;
	additionalNavItems?: NavItemProps[];
}) => {
	const items: NavItemProps[] = [];

	if (access.hasOperatorAccess) {
		items.push(
			{
				label: "Inventory Data",
				path: "/operator/inventory-data",
				icon: <InventoryIcon width="22" height="22" />,
				getIsCurrentNavLocation: (pathname) =>
					pathname?.includes("/operator/inventory-data") ||
					pathname?.includes("/upload"),
			},
			{
				label: "Emissions Analysis",
				path: "/operator/emissions-analysis",
				icon: <BubbleChartOutlinedIcon sx={{ fontSize: 22 }} />,
			}
		);
	}

	if (access.hasSCMAccess) {
		items.push({
			label: "Supply Chain",
			path: "/supplyChainManager/overview",
			icon: <TruckIcon width="22" height="22" />,
		});
	}

	if (access.hasSCMAccess || access.hasOperatorAccess) {
		items.push({
			label: "Reports",
			path: "/reports",
			icon: <FileChartIcon width="22" height="22" />,
		});
	}

	if (access.hasRespondAccess) {
		items.push(
			{
				label: "Response Hub",
				path: "/response-hub",
				icon: <ListIcon viewBox="0 -3 24 24" />,
			},
			{
				label: "Ask Optera.AI",
				path: "/ask-ai",
				icon: <SparklesIcon width="22" height="22" />,
			}
		);
	}

	if (access.hasSurveyorAccess) {
		items.push({
			label: "Survey Dashboard",
			path: "/survey/survey-dashboard",
			icon: <SupplierOutreachIcon width="22" height="22" />,
		});
	}

	if (access.hasSurveyAccess) {
		items.push({
			label: "My Surveys",
			path: "/survey/campaign-select",
			icon: <ListAltIcon sx={{ fontSize: 22 }} />,
		});
	}

	if (access.hasApiKeyFeatureAccess) {
		items.push({
			label: "API Key Management",
			path: "/admin/api-keys",
			icon: <KeyIcon sx={{ fontSize: 22 }} />,
		});
	}
	{
		additionalNavItems?.map((item: NavItemProps) => (
			<NavItem key={item.label} {...item} />
		));
	}

	return (
		<ul aria-label="Side navigation menu" role="menubar">
			{items.map((item) => (
				<NavItem key={item.label} {...item} />
			))}
		</ul>
	);
};

const ProfileIcon = ({
	access,
	orgName,
}: {
	access: GenericObject;
	orgName: string;
}) => {
	const items: NavItemProps[] = [];
	if (access.hasRespondAccess) {
		items.push({
			label: `${orgName} Profile`,
			path: "/profile",
			icon: (
				<Avatar
					alt={orgName}
					sx={{
						width: 24,
						height: 24,
						color: "var(--oc-palette-text-primary)",
						bgcolor: "var(--oc-palette-purple-100)",
					}}
				>
					<span className="fs-14">{orgName[0]}</span>
				</Avatar>
			),
		});
	}
	return (
		<ul aria-label="Side navigation profile icon" role="menubar">
			{items.map((item) => (
				<NavItem key={item.label} {...item} />
			))}
		</ul>
	);
};

const BottomNavItems = ({
	access,
	additionalNavItems,
}: {
	access: GenericObject;
	additionalNavItems?: NavItemProps[];
}) => {
	const items: NavItemProps[] = [];

	if (access.hasSCMAccess || access.hasOperatorAccess) {
		items.push({
			label: "Give Feedback",
			path: "https://coda.io/form/Optera-Platform-Feedback-Form_d9ifVZ_WLCE",
			icon: <CommentIcon width="22" height="22" />,
			target: "_blank",
			rel: "noopener noreferrer",
		});
	}

	if (access.isAdmin) {
		items.push({
			label: "User Management",
			path: "/admin/users",
			icon: <PeopleOutlineIcon sx={{ fontSize: 22 }} />,
		});
	}

	additionalNavItems?.forEach((item) => items.push(item));

	if (access.hasSCMAccess || access.hasOperatorAccess) {
		items.push({
			label: "Help",
			path: access.hasOperatorAccess
				? "/facade/auth/owl-docs?r=/docs/uploading-a-utility-bill-for-automated-data-ingestion"
				: "/facade/auth/owl-docs?r=/docs/getting-started-with-supply-chain-manager",
			icon: <HelpIcon width="22" height="22" />,
			prefetch: false,
			target: "_blank",
			rel: "noopener noreferrer",
		});
	}

	if (access.isAdmin) {
		items.push({
			label: "Survey Management",
			path: "/survey/survey-management",
			icon: <FormatListBulletedIcon sx={{ fontSize: 22 }} />,
		});
	}

	return (
		<ul aria-label="Side navigation lower menu" role="menubar">
			{items.map((item) => (
				<NavItem key={item.label} {...item} />
			))}
		</ul>
	);
};

const AvatarExpansionTooltipWrapper = ({ children }) => {
	const $user = useStore(user);
	const $expanded = useStore(railExpandedStore);
	return $expanded ? (
		<div className="currentOrg">{children}</div>
	) : (
		<Tooltip arrow placement="right" title={$user.organization}>
			{children}
		</Tooltip>
	);
};

function LeftRail({ items = [], bottomItems = [] }: LeftRailProps) {
	const $user = useStore(user);
	const $expanded = useStore(railExpandedStore);
	const ChevronIconUse = $expanded ? ChevronLeftIcon : ChevronRightIcon;

	const access = {
		hasSurveyorAccess: $user.roles.includes("survey:surveyor"),
		hasSurveyAccess: "survey:surveyee" in $user.traits,
		hasSCMAccess: $user.endorsements.includes("supply_chain_insights:brand"),
		hasOperatorAccess: $user.endorsements.includes("operator:brand"),
		hasOperatorManagerAccess: $user.roles.includes("write:operator"),
		hasRespondAccess: $user.feature_flags.includes("respond"),
		hasApiKeyFeatureAccess:
			$user.roles.includes("read:apikey") &&
			$user.roles.includes("write:apikey"),
		isAdmin: $user.roles.includes("optera:admin"),
	};

	const orgName = $user.organization || "Loading...";

	return (
		<nav id="leftRail-nav" aria-label="Side navigation">
			<Tooltip
				arrow
				placement="right"
				title={$expanded ? "Collapse" : "Expand"}
			>
				<button
					type="button"
					className="toggle"
					onClick={toggleRail}
					aria-expanded={$expanded ? "true" : "false"}
					aria-controls="leftRail-nav"
					aria-label="Widen or narrow left side nav"
				>
					<ChevronIconUse style={{ fontSize: 20 }} />
				</button>
			</Tooltip>
			<Suspense fallback={<div>loading...</div>}>
				<div className="marginTransition">
					<div className={$expanded ? "" : "avatarMarginClosed"}>
						<AvatarExpansionTooltipWrapper>
							<Avatar
								variant="rounded"
								alt={orgName}
								aria-label="Org badge"
								className="companyAvatar"
							>
								{orgName[0]}
							</Avatar>
							{$expanded && <p className="body2 currentOrgName">{orgName}</p>}
						</AvatarExpansionTooltipWrapper>
					</div>
					<div className="topNav">
						<TopNavItems access={access} additionalNavItems={items} />
					</div>
					<div className="railFooter">
						<ProfileIcon access={access} orgName={orgName} />
						<Divider />
						<BottomNavItems access={access} additionalNavItems={bottomItems} />
					</div>
				</div>
			</Suspense>
		</nav>
	);
}

export default LeftRail;
